import React, {useEffect, useState} from "react";
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {KeyboardArrowDownOutlined, SearchOutlined} from "@mui/icons-material";
import {navigation, findTrail, isPathActive} from "../navigation";
import {selectCurrentUser} from "../../features/auth/authSlice";
import {hasAnyRole} from "../../features/auth/roles";
import "./sidebar.css";

const canSee = (item, user) => {
    if (!item.roles || item.roles.length === 0) {
        return true;
    }
    return hasAnyRole(user, item.roles);
};

const Sidebar = ({collapsed = false, mobileOpen = false, onClose, onOpenCommand}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector(selectCurrentUser);
    const [openGroups, setOpenGroups] = useState(() => {
        const trail = findTrail(location.pathname);
        return trail ? {[trail.group.id]: true} : {};
    });

    useEffect(() => {
        const trail = findTrail(location.pathname);
        if (trail) {
            setOpenGroups((prev) => ({...prev, [trail.group.id]: true}));
        }
        if (mobileOpen && onClose) {
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const toggleGroup = (groupId) => {
        setOpenGroups((prev) => ({...prev, [groupId]: !prev[groupId]}));
    };

    const visibleGroups = navigation
        .filter((group) => !group.hidden)
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => canSee(item, user))
        }))
        .filter((group) => group.items.length > 0);

    const handleParentClick = (item) => {
        if (collapsed) {
            const first = (item.children || []).find((child) => child.to);
            if (first) {
                navigate(first.to);
            }
            return;
        }
        toggleGroup(item.id);
    };

    return (
        <aside
            className={`ui-sidebar${collapsed ? " is-collapsed" : ""}${mobileOpen ? " is-mobile-open" : ""}`}
            aria-label="Главное меню"
        >
            <div className="ui-sidebar__brand">
                <NavLink to="/panel" className="ui-brand" onClick={onClose}>
                    <span className="ui-brand__mark" aria-hidden="true">i</span>
                    {!collapsed && (
                        <span className="ui-brand__text">
                            <span className="ui-brand__name">
                                <span className="ui-brand__name-accent">i</span>Study
                            </span>
                            <span className="ui-brand__caption">Admin Console</span>
                        </span>
                    )}
                </NavLink>
                {!collapsed && (
                    <button
                        type="button"
                        className="ui-sidebar__search"
                        onClick={onOpenCommand}
                        title="Поиск по разделам (Ctrl+K)"
                    >
                        <SearchOutlined fontSize="small"/>
                        <span>Поиск раздела</span>
                        <kbd>Ctrl K</kbd>
                    </button>
                )}
            </div>

            <nav className="ui-sidebar__nav">
                {visibleGroups.map((group) => (
                    <div className="ui-nav-group" key={group.id}>
                        {!collapsed && <div className="ui-nav-group__title">{group.title}</div>}
                        {collapsed && <div className="ui-nav-group__divider" aria-hidden="true"/>}
                        <ul className="ui-nav-group__list">
                            {group.items.map((item) => {
                                if (item.header) {
                                    return (
                                        <li key={item.id} className="ui-nav-section">
                                            {!collapsed && item.title}
                                        </li>
                                    );
                                }
                                const active = isPathActive(location.pathname, item.to) ||
                                    (item.children || []).some((child) => isPathActive(location.pathname, child.to));
                                const expanded = !!openGroups[item.id];
                                const hasChildren = Array.isArray(item.children) && item.children.length > 0;

                                if (hasChildren) {
                                    return (
                                        <li key={item.id} className="ui-nav-item">
                                            <button
                                                type="button"
                                                className={`ui-nav-link ui-nav-link--parent${active ? " is-active" : ""}`}
                                                onClick={() => handleParentClick(item)}
                                                aria-expanded={!collapsed ? expanded : undefined}
                                                title={collapsed ? item.title : undefined}
                                            >
                                                <span className="ui-nav-link__icon">{item.icon}</span>
                                                {!collapsed && (
                                                    <>
                                                        <span className="ui-nav-link__title">{item.title}</span>
                                                        <KeyboardArrowDownOutlined
                                                            className={`ui-nav-link__chevron${expanded ? " is-open" : ""}`}
                                                            fontSize="small"
                                                        />
                                                    </>
                                                )}
                                            </button>
                                            {!collapsed && expanded && (
                                                <ul className="ui-nav-submenu">
                                                    {item.children.filter((child) => child.to).map((child) => (
                                                        <li key={child.id}>
                                                            <NavLink
                                                                to={child.to}
                                                                className={({isActive}) => `ui-nav-sublink${isActive ? " is-active" : ""}`}
                                                                onClick={onClose}
                                                            >
                                                                {child.title}
                                                            </NavLink>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                }

                                return (
                                    <li key={item.id} className="ui-nav-item">
                                        <NavLink
                                            to={item.to}
                                            className={({isActive}) => `ui-nav-link${isActive ? " is-active" : ""}`}
                                            title={collapsed ? item.title : undefined}
                                            onClick={onClose}
                                        >
                                            <span className="ui-nav-link__icon">{item.icon}</span>
                                            {!collapsed && <span className="ui-nav-link__title">{item.title}</span>}
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </nav>

            {!collapsed && (
                <div className="ui-sidebar__footer">
                    <span className="ui-sidebar__version">iStudy Admin · v0.2</span>
                </div>
            )}
        </aside>
    );
};

export default Sidebar;

